/**
 * The template-variable system: the registry of `{{tokens}}` an author can
 * insert, the types that keep every key spelled the same everywhere, and the two
 * resolvers that substitute them.
 *
 * Split out of `constants.ts` — this is a subsystem with its own invariants, not
 * a bag of constants, and it was over half of that file. The jobs module binds
 * each key to an API field in `buildJobFillContext`; nothing else may.
 */

export type VariableGroup = 'Client' | 'Project' | 'Site' | 'Job';

/**
 * Variables the user can insert into text labels, QR content and text-input
 * placeholder/default. Keys mirror the Dyna-ERP-API fields these resolve from:
 * client.name/sql_code, project.name/sql_project_code/contract_type/
 * project_type_name, site.name/sql_project_code/site_code/address/region
 * (its display name)/client_site_id,
 * job.title/service_type/scheduled_start/actual_start/assigned technicians. They
 * render as the literal {{token}} in the editor and are filled from the attached
 * job's data in the job tab — see `buildJobFillContext` in the jobs module, which
 * is the one place a key is bound to a source field.
 *
 * THIS LIST IS THE REGISTRY. `VariableKey` is derived from it, `VariableValues`
 * is keyed by that, and `variableToken` derives the insertable token — so a key not
 * spelled the same everywhere is a compile error rather than a variable the
 * picker offers and nothing ever fills (DERP-235).
 *
 * `project_contract_type` and `project_category` are DIFFERENT project fields and
 * both are offered — the same split the project form collects (ProjectForm.tsx):
 * contract type is the commercial arrangement (Ad-hoc / Annual / Project-based /
 * Retainer, `CONTRACT_TYPE_VALUES`), category is the admin-configurable
 * `ProjectType` lookup (DERP-156), read off the denormalized `project_type_name`.
 * Rows predating DERP-156 hold project-type strings in the free-text
 * `contract_type` column, so on those projects {{project_contract_type}} renders a
 * category-looking value. That is stale DATA, not a mis-wired token — see the note
 * on CONTRACT_TYPE_VALUES in modules/projects/lib/display.ts.
 *
 * `site_sql_code` and `project_code` both read a field named `sql_project_code`,
 * on the site and on the project respectively. The site's is the whole SQL Account
 * code (`{CAT-PROJECT}-{CLIENT}-{SITE}`, e.g. `OFF-FSOP-M001-ABNG`) and is read
 * AS-IS — never recomposed from the project code plus `site_code`, because the
 * day-1 master-data cleanup exists precisely because those two do not always tally
 * and a recomposed string would paper over the mismatch. `site_code` stays the
 * trailing site segment on its own, so a template can print either. Same rule as
 * the leading column on the sites registry (DERP-242).
 */
export const TEMPLATE_VARIABLES = [
  {key: 'client_name', label: 'Client Name', group: 'Client'},
  {key: 'client_sql_code', label: 'Client SQL Code', group: 'Client'},
  {key: 'project_name', label: 'Project Name', group: 'Project'},
  {key: 'project_code', label: 'SQL Project Code', group: 'Project'},
  {key: 'project_contract_type', label: 'Contract Type', group: 'Project'},
  {key: 'project_category', label: 'Project Category', group: 'Project'},
  {key: 'site_name', label: 'Site Name', group: 'Site'},
  {key: 'site_sql_code', label: 'Site SQL Code', group: 'Site'},
  {key: 'site_code', label: 'Site Code', group: 'Site'},
  {key: 'site_address', label: 'Site Address', group: 'Site'},
  {key: 'site_region', label: 'Site Region', group: 'Site'},
  {key: 'site_client_site_id', label: 'Client Site ID', group: 'Site'},
  {key: 'job_title', label: 'Job Title', group: 'Job'},
  {key: 'job_service_type', label: 'Service Type', group: 'Job'},
  {key: 'job_scheduled_date', label: 'Scheduled Date', group: 'Job'},
  // When the crew actually arrived, not when the job was planned: `actual_start`,
  // which `POST /jobs/{id}/check-in` stamps. Date AND time, where the scheduled
  // date above is a date alone — a photo report is evidence of when the work
  // happened. Unset until check-in, which is exactly when this report opens.
  {key: 'job_check_in_time', label: 'Check-in Time', group: 'Job'},
  {key: 'job_assigned_users', label: 'Assigned Technicians', group: 'Job'},
] as const satisfies readonly {key: string; label: string; group: VariableGroup}[];

/**
 * Every key the registry offers. Derived from the registry rather than declared
 * beside it, so the two cannot disagree — and every fill-context key is checked
 * against it at compile time.
 */
export type VariableKey = (typeof TEMPLATE_VARIABLES)[number]['key'];

/**
 * The groups the picker renders, in registry order.
 *
 * Derived, not listed. A hand-written copy typed `VariableGroup[]` accepts a
 * SUBSET without complaint, so a group left out of it would silently drop every
 * variable in it from the picker — an author unable to insert a variable that
 * exists, which is the DERP-235 defect one level up. Every group here has at
 * least one variable by construction.
 */
export const VARIABLE_GROUPS: readonly VariableGroup[] = [
  ...new Set(TEMPLATE_VARIABLES.map((v) => v.group)),
];

/**
 * The insertable token for a key — `{{client_name}}`.
 *
 * Derived, never stored on the entry. The picker inserts this and the fill
 * context is keyed by `key`; a second, hand-written copy of the spelling on each
 * entry is exactly what can drift out of step with it (DERP-235).
 */
export function variableToken(key: VariableKey): string {
  return `{{${key}}}`;
}

/**
 * Variable values, keyed by `VariableKey`. A variable with no value is ABSENT,
 * never empty — see `FillContext`.
 *
 * Deliberately NOT named `FillValues`: the jobs module already has a type by
 * that name for something else entirely (requirement id → the crew's answer, in
 * `report-requirements.ts`), and both are in scope in the report-fill flow.
 */
export type VariableValues = Partial<Record<VariableKey, string>>;

/** What a report shows for a variable the job has no value for. */
export const MISSING_VARIABLE_PLACEHOLDER = '-';

/**
 * The two readings of one job's variable values, built together so a caller
 * cannot accidentally use the wrong one.
 *
 * They answer different questions and both are needed: DISPLAY must never leak
 * `{{token}}` onto a document a client may see, while seeding an input has to be
 * able to tell "the job has no site" from "the site is called -".
 */
export interface FillContext {
  /** Exactly what the job supplied. A variable it has no value for is ABSENT. */
  job: VariableValues;
  /** The same, with the placeholder for every known-but-unset variable — so
   *  every key the registry offers is present, by construction. */
  display: Record<VariableKey, string>;
}

/** Build both readings of a job's variable values. See `FillContext`. */
export function buildFillContext(job: VariableValues): FillContext {
  return {job, display: fillMissingVariables(job)};
}

/**
 * Give every KNOWN variable the job didn't supply the placeholder, so a report
 * never shows a raw `{{token}}` to a crew.
 *
 * This is the DISPLAY half of a `FillContext`. Applying it once at the context
 * boundary is what lets every renderer inherit it for free — labels, input
 * placeholders, QR content and table cell content alike — with no flag threaded
 * through them, and it keeps the two audiences separated:
 *
 *  - The EDITOR passes no context at all, so its tokens still render literally —
 *    an author needs to see which variable a field carries.
 *  - A JOB report passes a filled context, so an unset variable reads as "-".
 *
 * Seeding deliberately does NOT read this half — see `seedableDefault` in the
 * jobs module, which reads `FillContext.job` so a half-resolved default doesn't
 * pre-fill an input with a dash the crew never typed.
 *
 * Only known variables are filled. A token that matches no variable is left
 * alone deliberately: `{{site_adress}}` is a typo, and rendering it as "-" would
 * hide the mistake instead of surfacing it.
 */
function fillMissingVariables(context: VariableValues): Record<VariableKey, string> {
  // The one cast: TypeScript cannot prove a build-up loop covers every key, so
  // the accumulator is asserted complete once the loop that fills it has run.
  const out = {} as Record<VariableKey, string>;
  for (const v of TEMPLATE_VARIABLES) {
    out[v.key] = context[v.key] || MISSING_VARIABLE_PLACEHOLDER;
  }
  return out;
}

/**
 * The {{token}} grammar. Declared once: `resolveVariables` substitutes against
 * it and `tryResolveVariables` detects survivors with it, so the two can never
 * disagree about what counts as a token.
 */
const VARIABLE_TOKEN = /\{\{(\w+)\}\}/g;

const REGISTERED_KEYS: ReadonlySet<string> = new Set(TEMPLATE_VARIABLES.map((v) => v.key));

/**
 * The one place a key parsed out of `{{…}}` meets the typed value map.
 *
 * A token's key is whatever an author typed, so it arrives as a bare string.
 * Narrowing it here — rather than widening `VariableValues` to accept any string
 * — keeps "this token names no variable" an explicit answer instead of an
 * indistinguishable missing-key lookup. `{{site_adress}}` is a typo, and both
 * resolvers below depend on being able to tell it from a variable that is merely
 * unset.
 */
function lookupVariable(context: VariableValues, key: string): string | undefined {
  return REGISTERED_KEYS.has(key) ? context[key as VariableKey] : undefined;
}

/**
 * Replace every {{key}} token in `text` with the matching value from `context`
 * (keyed by variable key). Unknown tokens are left untouched — so in the editor
 * (empty context) the literal {{token}} shows through, and in the job tab the
 * filled value appears. See `fillMissingVariables` for how the display half of a
 * `FillContext` makes an unset-but-known variable resolve to "-".
 */
export function resolveVariables(text: string, context: VariableValues): string {
  return text.replace(
    VARIABLE_TOKEN,
    (match, key: string) => lookupVariable(context, key) ?? match,
  );
}

/**
 * `resolveVariables`, but undefined unless EVERY token resolved.
 *
 * Callers that pre-fill a value (rather than display one) need the difference: a
 * partly-resolved string is not a value the job supplied, and seeding it would
 * put text in an input that nobody entered. A token matching no known variable
 * fails the same way — `{{site_adress}}` is an authoring typo and should surface
 * as an empty field, not get pre-filled.
 */
export function tryResolveVariables(text: string, context: VariableValues): string | undefined {
  let missing = false;
  const resolved = text.replace(VARIABLE_TOKEN, (match, key: string) => {
    const value = lookupVariable(context, key);
    if (value === undefined) {
      missing = true;
      return match;
    }
    return value;
  });
  return missing ? undefined : resolved;
}
